import { useRef, useState } from 'react';
import { useSpring } from 'react-spring';
import { useGesture } from 'react-use-gesture';
import clamp from 'lodash/clamp';
import { useZoomClickGestureHandlers } from '../../hooks';
import { useGalleryGestureLock } from '../../context/GalleryGestureLock';
import { Transformation, Vector } from './useZoom.types';
import {
    combineTransformations,
    emptyTransformation,
    interpolateSpringToCSSTransformValue
} from './useZoom.helpers';

const ZOOM_FACTOR_DEFAULT = 3; // Scale the image to this ratio on interaction
const ZOOM_FACTOR_MAX = 7; // Don't allow the user to scale the image above this ratio
const ZOOM_FACTOR_MIN = 1.3; // Scale ratio at which we consider the image to be near-enough unscaled and snap back to 1x
const ZOOM_FACTOR_PINCH_ZOOM_MIN = 0.8; // Minimum scale we allow the user to pinch-zoom (gesture only, ZOOM_FACTOR_MIN still applies after)

const useZoom = (
    isZoomEnabled: boolean,
    viewportRef: React.RefObject<HTMLElement>,
    allowMotion: boolean,
    onSlideClick: (isZoomed: boolean) => void
) => {
    // Underscored setter because we don't want to accidentally
    // call this directly without some additional wrapping logic
    const [isZoomed, _setIsZoomedState] = useState(false);

    // Ideal image width we'd load is (slide width) * (max zoom factor)
    const [optimalImageWidthForZoom, setOptimalImageWidthForZoom] = useState(0);

    // We need to allow for both single click and double click events
    // which isn't natively possible
    const customClickGestureHandlers = useZoomClickGestureHandlers();

    // We're not the only part of the gallery listening for gestures. We use
    // this shared module to lock our interactions to ensure we don't conflict.
    // Note: this is imperative (i.e. outside of react)
    const gestureLock = useGalleryGestureLock();

    // We need to map screen coordinates to gallery coordinates
    const viewportCoordinates = useRef<{
        top: number;
        left: number;
        width: number;
        height: number;
    }>({ top: 0, left: 0, width: 0, height: 0 });

    // The transformation for the image is applied in two transformations. The first is
    // the existing transformation. This is the current state of the transformation
    // when no gestures are active.
    //
    // When a gesture begins, we update the spring on each gesture step by setting the
    // active transformation values. Once a gesture is complete, we calculate a new
    // existing transformation by merging the active into it.
    const [transforms, setSpring] = useSpring(() => {
        return {
            activeOrigin: [0, 0],
            activeTranslation: [0, 0],
            activeScale: 1,
            existingTranslation: [0, 0],
            existingScale: 1,
            config: {
                mass: 1,
                tension: 170,
                friction: 20,
                clamp: true
            }
        };
    });

    // We treat the spring as write-only. The destination values of any transformation
    // are stored here and treated as the authority. We're using a ref as all of this
    // happens outside of the react rendering lifecycle.
    const { current: transformation } = useRef<{
        existing: Transformation;
        active: Transformation;
    }>({
        existing: emptyTransformation(),
        active: emptyTransformation()
    });

    const getSlideContainerCoordinates = () => viewportCoordinates.current;

    const setIsZoomed = (bool: boolean) => {
        _setIsZoomedState(bool);
        gestureLock.setZoomed(bool);
    };

    const refreshViewportCoordinates = () => {
        if (!viewportRef.current) {
            return;
        }

        const { top, left, width, height } =
            viewportRef.current.getBoundingClientRect();

        viewportCoordinates.current = { top, left, width, height };
        setOptimalImageWidthForZoom(width * ZOOM_FACTOR_MAX);
    };

    const setSpringToTransformationValues = (immediate = true) => {
        setSpring({
            activeOrigin: transformation.active.origin,
            activeScale: transformation.active.scale,
            activeTranslation: transformation.active.translation,
            existingTranslation: transformation.existing.translation,
            existingScale: transformation.existing.scale,
            immediate: immediate || !allowMotion
        });
    };

    const calculateMaximumDragGestureBounds = () => {
        const top = -(
            getSlideContainerCoordinates().height *
                (transformation.existing.scale - 1) +
            transformation.existing.translation[1]
        );

        const bottom = 0 - transformation.existing.translation[1];
        const left = -(
            getSlideContainerCoordinates().width *
                (transformation.existing.scale - 1) +
            transformation.existing.translation[0]
        );
        const right = 0 - transformation.existing.translation[0];

        return {
            top,
            bottom,
            left,
            right
        };
    };

    // Resets zoom level to 1x, optionally animated
    const zoomOut = (immediate = false) => {
        transformation.existing = emptyTransformation();
        transformation.active = emptyTransformation();

        setSpringToTransformationValues(immediate);
        setIsZoomed(false);
    };

    const zoomIn = (coords: Vector, immediate = false) => {
        refreshViewportCoordinates();

        transformation.existing = combineTransformations(
            transformation.existing,
            {
                origin: coords,
                translation: [0, 0],
                scale: ZOOM_FACTOR_DEFAULT
            }
        );
        transformation.active = emptyTransformation();

        // Animate to new zoom factor
        setSpringToTransformationValues(immediate);

        setIsZoomed(true);
    };

    const zoomInToCenter = (immediate = false) => {
        refreshViewportCoordinates();

        const coords: Vector = [
            viewportCoordinates.current.width / 2,
            viewportCoordinates.current.height / 2
        ];

        zoomIn(coords, immediate);
    };

    const screenCoordinatesToSlideCoordinates = (
        screenCoords: Vector
    ): Vector => {
        const { top, left } = getSlideContainerCoordinates();

        return [screenCoords[0] - left, screenCoords[1] - top];
    };

    const onDoubleTap = (screenCoords: Vector) => {
        if (isZoomed || !isZoomEnabled) {
            return zoomOut();
        }

        refreshViewportCoordinates();
        zoomIn(screenCoordinatesToSlideCoordinates(screenCoords));
    };

    const touchEventHandlers = useGesture(
        {
            //
            // Drag
            //
            onDragStart: () => {
                if (!gestureLock.lock('pan zoom')) {
                    return;
                }
                refreshViewportCoordinates();
            },

            onDrag: ({ movement }) => {
                if (gestureLock.getActiveInteraction() !== 'pan zoom') {
                    return;
                }

                transformation.active.translation = movement;
                setSpringToTransformationValues(true);
            },

            onDragEnd: () => {
                if (gestureLock.getActiveInteraction() !== 'pan zoom') {
                    return;
                }

                // Merge the completed drag transformation in to the existing
                // transformation and update the spring
                transformation.existing = combineTransformations(
                    transformation.existing,
                    transformation.active
                );
                transformation.active = emptyTransformation();
                setSpringToTransformationValues();
                gestureLock.unlock('pan zoom');
            },

            //
            // Pinch
            //
            onPinchStart: ({ origin }) => {
                if (!gestureLock.lock('pinch zoom')) {
                    return;
                }

                setIsZoomed(true);
                refreshViewportCoordinates();

                const touch: Vector =
                    screenCoordinatesToSlideCoordinates(origin);

                transformation.active.origin = touch;
                setSpringToTransformationValues(true);
            },

            onPinch: ({ initial, da: [distance], _lastEventType }) => {
                if (gestureLock.getActiveInteraction() !== 'pinch zoom') {
                    return;
                }

                // Trackpad/mousewheel zoom behaviour is somewhat buggy currently. We're disabling
                // pinch events originating from these inputs until it can be improved.
                if (_lastEventType === 'wheel') {
                    return;
                }

                // Zoom limits are calculated relative to the existing transformation scale
                // (i.e. if existing scale is already 5x, and the maximum allowed is 10x then this
                // active scale must be limited to 2x)
                const minZoom =
                    1 / transformation.existing.scale -
                    (1 - ZOOM_FACTOR_PINCH_ZOOM_MIN);
                const maxZoom = ZOOM_FACTOR_MAX / transformation.existing.scale;

                const pinchLength = distance / initial[0];
                const scale = clamp(pinchLength, minZoom, maxZoom);

                transformation.active.scale = scale;
                setSpringToTransformationValues(true);
            },

            onPinchEnd: () => {
                if (gestureLock.getActiveInteraction() !== 'pinch zoom') {
                    return;
                }

                transformation.existing = combineTransformations(
                    transformation.existing,
                    transformation.active
                );
                transformation.active = emptyTransformation();
                setSpringToTransformationValues(true);

                // When the pinch leaves the zoom factor at a low level,
                // we treat it as near enough zoomed-out so bounce back to 1x
                if (transformation.existing.scale <= ZOOM_FACTOR_MIN) {
                    zoomOut();
                }
                gestureLock.unlock('pinch zoom');
            },

            //
            // Click (handles disambiguation between two single clicks and a double click)
            //
            ...customClickGestureHandlers(
                // Called on single click
                () => onSlideClick(isZoomed),

                // Called on double-click
                (event: Event) => {
                    if (!event || !gestureLock.lock('double tap zoom')) {
                        return;
                    }
                    const e = event as unknown as MouseEvent;

                    onDoubleTap([e.clientX, e.clientY]);
                    gestureLock.unlock('double tap zoom');
                }
            )
        },
        {
            drag: {
                filterTaps: true,
                bounds: calculateMaximumDragGestureBounds
            }
        }
    );

    return {
        touchEventHandlers,
        buildCSSTransform: () =>
            interpolateSpringToCSSTransformValue(transforms),
        isZoomed,
        optimalImageWidthForZoom,
        controls: {
            zoomIn: (isAnimated = false) => zoomInToCenter(!isAnimated),
            zoomOut: (isAnimated = false) => zoomOut(!isAnimated)
        }
    };
};

export default useZoom;
